// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

import React, {
  useContext,
  createContext,
  useState,
  MouseEventHandler,
  useEffect
} from 'react';
import { AuthCodeMSALBrowserAuthenticationProvider } from '@microsoft/microsoft-graph-client/authProviders/authCodeMsalBrowser';
import { InteractionType, PublicClientApplication } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';

import { getList, getUser,getListColumnName,getDocNamefromLibrary,getDocContent } from './GraphService';
import config from './Config';

// <AppContextSnippet>
export interface AppUser {
  displayName?: string,
  email?: string,
  avatar?: string,
  timeZone?: string,
  timeFormat?: string
};

export interface AppError {
  message: string,
  debug?: string
};

type AppContext = {
  user?: AppUser;
  error?: AppError;
  signIn?: MouseEventHandler<HTMLElement>;
  signOut?: MouseEventHandler<HTMLElement>;
  displayError?: Function;
  clearError?: Function;
  authProvider?: AuthCodeMSALBrowserAuthenticationProvider;
  finalcolumn?:any
  docName?:any
  editorContant?:any
}

const appContext = createContext<AppContext>({
  user: undefined,
  error: undefined,
  signIn: undefined,
  signOut: undefined,
  displayError: undefined,
  clearError: undefined,
  authProvider: undefined,
  finalcolumn:undefined,
  docName:undefined,
  editorContant:undefined
});
let columdata=[];
let DocData=[];
export function useAppContext(): AppContext {
  return useContext(appContext);
}

interface ProvideAppContextProps {
  children: React.ReactNode;
}

export default function ProvideAppContext({ children }: ProvideAppContextProps) {
  const auth = useProvideAppContext();
  return (
    <appContext.Provider value={auth}>
      {children}
    </appContext.Provider>
  );
}
// </AppContextSnippet>

function useProvideAppContext() {
  const msal = useMsal();
  const [user, setUser] = useState<AppUser | undefined>(undefined);
  const [error, setError] = useState<AppError | undefined>(undefined);
  const [finalcolumn, setfinalcolumn] = React.useState([]);
  const [docName, setdocName] = React.useState([]);
  const [editorContant, seteditorContant] = React.useState([]);
  const displayError = (message: string, debug?: string) => {
    setError({ message, debug });
  }

  const clearError = () => {
    setError(undefined);
  }

  // <AuthProviderSnippet>
  // Used by the Graph SDK to authenticate API calls
  const authProvider = new AuthCodeMSALBrowserAuthenticationProvider(
    msal.instance as PublicClientApplication,
    {
      account: msal.instance.getActiveAccount()!,
     // account:John Taylor,
      scopes: config.scopes,
      interactionType: InteractionType.Popup
    }
  );
  // console.log(authProvider,"authProvider")
  // </AuthProviderSnippet>

  // <UseEffectSnippet>
  useEffect(() => {
    const checkUser = async () => {
      if (!user) {
        try {
          // Check if user is already signed in
          const account = msal.instance.getActiveAccount();
          if (account) {
            // Get the user from Microsoft Graph
            const user = await getUser(authProvider);

            setUser({
              displayName: user.displayName || '',
              email: user.mail || user.userPrincipalName || '',
              timeFormat: user.mailboxSettings?.timeFormat || 'h:mm a',
              timeZone: user.mailboxSettings?.timeZone || 'UTC'
            });
            const list = await getList(authProvider);
            // console.log(list);
         
            const listcol:any = await getListColumnName(authProvider).then((items:any)=>{
            // console.log(items);
            Object.entries(items.value).map(([i,key])=>
            {let index =i;
              if(key!=undefined && key!=null){
              (key['readOnly']==false)?   
            columdata.push({displayname:key['displayName'] ,internalname:key['name']})
            :""}
            }) 
            setfinalcolumn(columdata);
            // items.value.map((i)=>{(i.ReadOnlyField==false)?
            //   columdata.push({displayname:i.displayName ,internalname:i.name}):""});
            });
          
            const docNames:any = await getDocNamefromLibrary(authProvider).then((items:any)=>{
              // console.log(items);
              DocData=[];
              Object.entries(items.value).map(([i,key])=>
              { 
                  console.log(i,"i")
              let docNm=key['name'].split('.')
              let downloadUrl=key['@microsoft.graph.downloadUrl']
              DocData.push({docNames:docNm[0] ,internalname:downloadUrl})
             
              }
              ) 
            setdocName(DocData);
              // items.value.map((i)=>{(i.ReadOnlyField==false)?
              //   columdata.push({displayname:i.displayName ,internalname:i.name}):""});
              });
              const DocContents:any =await getDocContent(authProvider).then((items:any)=>{
                // console.log(items,"content");
                seteditorContant(items);
                
           
                });
            // console.log(DocContents,"DocContents");
            // console.log(listcol,"listcol");
            // console.log(DocData,"DocData");
            // console.log(docNames,"docNames");
         
          }
        } catch (err: any) {
          displayError(err.message);
        }
      }
    };
    checkUser();
  });
  // </UseEffectSnippet>

  // <SignInSnippet>
  const signIn = async () => {
    await msal.instance.loginPopup({
      scopes: config.scopes,
      prompt: 'select_account'
    });

    // Get the user from Microsoft Graph
    const user = await getUser(authProvider);

    setUser({
      displayName: user.displayName || '',
      email: user.mail || user.userPrincipalName || '',
      timeFormat: user.mailboxSettings?.timeFormat || '',
      timeZone: user.mailboxSettings?.timeZone || 'UTC'
    });
  };
  // </SignInSnippet>

  // <SignOutSnippet>
  const signOut = async () => {
    await msal.instance.logoutPopup();
    setUser(undefined);
  };
  // </SignOutSnippet>

  return {
    user,
    error,
    signIn,
    signOut,
    displayError,
    clearError,
    authProvider,
    finalcolumn,
    docName,
    editorContant
  };
}
